import React, { useContext, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSitemap, useRequestData, useLabels } from 'wsm-common-data';
import { trackEvent } from 'ddc-track-event';
import { AccountContext, UIContext } from '../contexts';
import buildInventoryUrl from '../utils/buildInventoryUrl';
import { requestMoreInfoEvent, viewInventoryEvent } from '../utils/wueTracking';

const ActionButtons = ({ offer, vehicle }) => {
	const labels = useLabels();
	const sitemap = useSitemap();
	const { widgetName, windowId } = useRequestData();
	const { selectedAccountId, isGroupSite } = useContext(AccountContext);
	const { isMobile } = useContext(UIContext);

	// Notify DDC.modules.dialog to bind the .dialog anchor after React renders it
	useEffect(() => {
		window.jQuery?.(document).trigger('modulesRefresh');
	}, []);

	const inventoryUrl = buildInventoryUrl({
		offer,
		sitemap,
		selectedAccountId,
		isGroupSite
	});

	const accountParam = encodeURIComponent(
		offer.accountId || selectedAccountId || ''
	);
	const requestInfoUrl =
		'/global-incentives/request-form.htm' +
		`?selectedAccountId=${accountParam}` +
		`&globalVehicleId=${encodeURIComponent(vehicle.globalVehicleId)}` +
		`&selectedIncentiveId=${encodeURIComponent(offer.incentiveId)}` +
		`&incentiveId=${encodeURIComponent(offer.incentiveId)}` +
		'&parentPageAlias=INCENTIVES_SEARCH_DETAIL';

	const viewInventoryLabel = labels.get('INCENTIVES_VIEW_INVENTORY');
	const requestInfoLabel = labels.get('INCENTIVES_REQUEST_INFO');

	const handleViewInventoryClick = useCallback(() => {
		trackEvent(
			widgetName,
			windowId,
			viewInventoryEvent({
				label: viewInventoryLabel,
				originView: 'detail'
			})
		);
	}, [widgetName, windowId, viewInventoryLabel]);

	const handleRequestInfoClick = useCallback(() => {
		trackEvent(
			widgetName,
			windowId,
			requestMoreInfoEvent({ label: requestInfoLabel })
		);
	}, [widgetName, windowId, requestInfoLabel]);

	return (
		<div className="d-flex gap-2 flex-column flex-md-row mt-3">
			<a
				href={requestInfoUrl}
				className={`btn btn-default dialog mr-3${
					isMobile ? ' mb-3' : ''
				}`}
				data-width="800"
				aria-label={`${requestInfoLabel} about ${
					offer.i18NShortTitle || offer.i18NTitle || 'this offer'
				}`}
				onClick={handleRequestInfoClick}
			>
				{requestInfoLabel}
			</a>
			<a
				href={inventoryUrl}
				className="btn btn-default mr-3"
				aria-label={`${viewInventoryLabel} for ${vehicle.year} ${vehicle.make} ${vehicle.model}`}
				onClick={handleViewInventoryClick}
			>
				{viewInventoryLabel}
			</a>
		</div>
	);
};

ActionButtons.propTypes = {
	offer: PropTypes.shape({
		incentiveId: PropTypes.string.isRequired,
		accountId: PropTypes.string,
		applicableConditionString: PropTypes.string,
		i18NShortTitle: PropTypes.string,
		i18NTitle: PropTypes.string
	}).isRequired,
	vehicle: PropTypes.shape({
		globalVehicleId: PropTypes.string,
		year: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		make: PropTypes.string,
		model: PropTypes.string
	}).isRequired
};

export default ActionButtons;
